/**
 * ACSR Government Medical College Website Style Guide
 * 
 * This file contains standardized colors, typography, spacing, and component styles
 * to maintain consistency across the entire website.
 */

// Color Palette
export const colors = {
  // Primary Colors
  primary: '#2E3192',        // Main brand color - Deep Blue
  primaryLight: 'rgba(46, 49, 146, 0.1)',  // Light version for backgrounds
  primaryDark: '#1E2075',    // Darker version for hover states
  
  // Secondary Colors
  secondary: '#0056b3',      // Secondary brand color - Medium Blue
  accent: '#f8f9fa',         // Light gray for backgrounds
  
  // Text Colors
  textPrimary: '#333333',    // Main text color
  textSecondary: '#555555',  // Secondary text color
  textLight: '#ffffff',      // Light text color (for dark backgrounds)
  
  // Background Colors
  bgLight: '#ffffff',        // White background
  bgGray: '#f8f9fb',         // Light gray background
  bgDark: '#1a1a2e',         // Dark background (footer)
  
  // Status Colors
  success: '#28a745',        // Success/Positive
  warning: '#ffc107',        // Warning/Caution
  danger: '#dc3545',         // Error/Danger
  info: '#17a2b8',           // Information
  
  // Overlay Colors
  overlay: 'rgba(32, 32, 96, 0.85)',  // Hero section overlay
};

// Typography
export const typography = {
  fontFamily: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  fontSizes: {
    xs: '0.85rem',
    sm: '0.95rem',
    base: '1rem',
    md: '1.2rem',
    lg: '1.4rem',
    xl: '1.8rem',
    xxl: '2.2rem',
    xxxl: '2.8rem',
  },
  fontWeights: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
  },
  lineHeights: {
    tight: 1.2,
    base: 1.6,
    relaxed: 1.8,
  },
};

// Spacing
export const spacing = {
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  xxl: '3rem',     // 48px
  xxxl: '5rem',    // 80px
};

// Border Radius
export const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  round: '50%',
};

// Shadows
export const shadows = {
  sm: '0 2px 10px rgba(0, 0, 0, 0.05)',
  md: '0 5px 15px rgba(0, 0, 0, 0.08)',
  lg: '0 10px 25px rgba(0, 0, 0, 0.1)',
  xl: '0 15px 30px rgba(0, 0, 0, 0.15)',
};

// Transitions
export const transitions = {
  default: 'all 0.3s ease',
  fast: 'all 0.2s ease',
  slow: 'all 0.5s ease',
};

// Component-specific styles
export const componentStyles = {
  // Buttons
  buttons: {
    primary: {
      backgroundColor: colors.primary,
      color: colors.textLight,
      borderColor: colors.primary,
      padding: '0.6rem 1.5rem',
      fontWeight: typography.fontWeights.medium,
      borderRadius: borderRadius.sm,
      transition: transitions.default,
      hover: {
        backgroundColor: colors.primaryDark,
        borderColor: colors.primaryDark,
      },
    },
    secondary: {
      backgroundColor: 'transparent',
      color: colors.primary,
      borderColor: colors.primary,
      padding: '0.6rem 1.5rem',
      fontWeight: typography.fontWeights.medium,
      borderRadius: borderRadius.sm,
      transition: transitions.default,
      hover: {
        backgroundColor: colors.primary,
        color: colors.textLight,
      },
    },
    light: {
      backgroundColor: colors.bgLight,
      color: colors.primary,
      borderColor: colors.bgLight,
      padding: '0.6rem 1.5rem',
      fontWeight: typography.fontWeights.medium,
      borderRadius: borderRadius.sm,
      transition: transitions.default,
      hover: {
        backgroundColor: colors.accent,
        borderColor: colors.accent,
      },
    },
  },
  
  // Cards
  cards: {
    default: {
      backgroundColor: colors.bgLight,
      borderRadius: borderRadius.md,
      padding: spacing.lg,
      boxShadow: shadows.sm,
      transition: transitions.default,
      hover: {
        boxShadow: shadows.md,
        transform: 'translateY(-3px)',
      },
    },
    highlight: {
      backgroundColor: colors.bgLight,
      borderRadius: borderRadius.md,
      padding: spacing.lg,
      boxShadow: shadows.sm,
      transition: transitions.default,
      hover: {
        boxShadow: shadows.md,
        transform: 'translateY(-5px)',
      },
    },
  },
  
  // Icons
  icons: {
    circle: {
      width: '70px',
      height: '70px',
      backgroundColor: colors.primaryLight,
      borderRadius: borderRadius.round,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.primary,
      fontSize: '28px',
    },
    small: {
      width: '50px',
      height: '50px',
      backgroundColor: colors.primaryLight,
      borderRadius: borderRadius.round,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.primary,
      fontSize: '16px',
    },
  },
  
  // Sections
  sections: {
    default: {
      padding: `${spacing.xxxl} 0`,
    },
    light: {
      backgroundColor: colors.bgGray,
      padding: `${spacing.xxxl} 0`,
    },
    dark: {
      backgroundColor: colors.bgDark,
      color: colors.textLight,
      padding: `${spacing.xxxl} 0`,
    },
  },
  
  // Navigation
  navigation: {
    link: {
      color: colors.textPrimary,
      fontWeight: typography.fontWeights.medium,
      transition: transitions.default,
      hover: {
        color: colors.primary,
      },
    },
    active: {
      color: colors.primary,
      position: 'relative',
      after: {
        content: "''",
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '2px',
        backgroundColor: colors.primary,
      },
    },
  },
  
  // Hero Section
  hero: {
    padding: '140px 0 150px',
    minHeight: '600px',
    color: colors.textLight,
    overlay: colors.overlay,
    title: {
      fontSize: typography.fontSizes.xxxl,
      fontWeight: typography.fontWeights.extraBold,
      letterSpacing: '1px',
      lineHeight: typography.lineHeights.tight,
      textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
      textTransform: 'uppercase',
    },
    subtitle: {
      fontSize: typography.fontSizes.lg,
      fontWeight: typography.fontWeights.regular,
      letterSpacing: '1px',
    },
  },
  
  // Carousel
  carousel: {
    arrows: {
      size: '45px',
      backgroundColor: colors.bgLight,
      color: colors.primary,
      borderRadius: borderRadius.round,
      boxShadow: shadows.md,
      hover: {
        backgroundColor: colors.bgLight,
        transform: 'translateY(-2px)',
        boxShadow: shadows.lg,
      },
    },
    dots: {
      size: '10px',
      color: '#ccc',
      activeColor: colors.primary,
    },
  },
};

const styleGuide = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  componentStyles,
};

export default styleGuide;
